import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Bar, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { farms } from '../components/List_farms';
import { Oval } from 'react-loader-spinner';
import { farmDataday } from '../components/farmDataday';
import ForecastCard from '../components/weather_forecast'; // 新しく作成したコンポーネントをインポート  
import { useLocation, useNavigate } from 'react-router-dom';
import {
    fetchYieldPrediction,
    fetchDailyYieldPrediction,
    fetchMarketforecast,
    fetchSensorData,
    fetchLastYearMarketData,
    fetchForecastWeatherData
} from './api'; // Import the functions from api.js  

// Styled Components  
const Container = styled.div`  
  display: flex;  
  flex-direction: column;  
  padding: 0 20px 60px 20px;  
  background: #fafafa;  
  width: 100%;  
  margin: auto;  
  font-family: Arial, sans-serif;  
  box-sizing: border-box;  
  justify-content: flex-start;  
  align-items: stretch;  
  min-height: 100vh;  
`;

const AiText = styled.div`  
  font-size: 24px;  
  font-weight: bold;  
  margin-top: 20px;  
  margin-bottom: 20px;  
`;

const InputContainer = styled.div`  
  display: flex;  
  flex-direction: column;  
  align-items: center;  
  margin-bottom: 20px;  
`;

const InputFieldsRow = styled.div`  
  display: flex;  
  gap: 10px;  
  justify-content: center;  
  & > * {  
    vertical-align: middle;  
  }  
`;

const InputFieldContainer = styled.div`  
  display: flex;  
  flex-direction: column;  
  align-items: center;  
`;

const InputLabel = styled.label`  
  font-size: 18px;  
  font-weight: bold;  
  margin-bottom: 5px;  
`;

const DateInput = styled.input`  
  padding: 10px;  
  font-size: 16px;  
  border: 1px solid #ccc;  
  border-radius: 5px;  
`;

const SelectField = styled.select`  
  padding: 10px;  
  font-size: 16px;  
  border: 1px solid #ccc;  
  border-radius: 5px;  
`;

const Button = styled.button`  
  margin-top: 10px;  
  width: 100%;  
  padding: 10px;  
  font-size: 16px;  
  background-color: green;  
  color: white;  
  border: none;  
  border-radius: 5px;  
  cursor: pointer;  
  align-self: center;  
`;

const ChartWrapper = styled.div`  
  background-color: #ffffff;  
  border-radius: 15px;  
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);  
  padding: 10px;  
  flex: 1;  
  min-height: 300px;  
  max-height: 300px;  
  margin-bottom: 20px;  
  @media (max-width: 768px) {  
    min-height: 300px;  
  }  
`;

const ChartContainer = styled.div`  
  width: 100%;  
  height: auto;  
`;

const LoaderOverlay = styled.div`  
  display: flex;  
  justify-content: center;  
  align-items: center;  
  position: absolute;  
  top: 0;  
  left: 0;  
  height: 25%;  
  width: 100%;  
  background-color: rgba(255, 255, 255, 0.8);  
  z-index: 1500;  
  flex-direction: column;  
`;

const ChartTitle = styled.h2`  
  font-size: 20px;  
  font-weight: bold;  
  text-align: center;  
  margin-bottom: 10px;  
`;

// Helper Functions  
// const generateLabelsFromDate = (startDate, days) => {
//     const date = new Date(startDate);
//     return Array.from({ length: days }, (_, i) => {
//         const newDate = new Date(date);
//         newDate.setDate(date.getDate() + i);
//         return `${newDate.getMonth() + 1}/${newDate.getDate()}`;
//     });
// };

const fillMissingDates = (data, startDate, endDate) => {
    if (!data || !Array.isArray(data)) {
        console.error('Invalid data provided to fillMissingDates:', data);
        return [];
    }
    const dateMap = new Map(data.map(item => [item.Date, item.kg]));
    const filledData = [];
    let currentDate = new Date(startDate);

    while (currentDate <= new Date(endDate)) {
        const dateString = currentDate.toISOString().split('T')[0];
        filledData.push({ Date: dateString, kg: dateMap.get(dateString) || 0 });
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return filledData;
};

// Main Component  
const Ai = () => {
    const location = useLocation();
    const { farmname, greenhouse, itemName } = location.state || {};
    const navigate = useNavigate();
    const abortControllerRef = useRef(new AbortController());
    const farm = farms.find(farm => farm.greenhouse === greenhouse);

    const [plantingDate, setPlantingDate] = useState('2024-09-09');
    const [temperature, setTemperature] = useState('');
    const [humidity, setHumidity] = useState('');
    const [co2, setCo2] = useState('');
    const [yieldPrediction, setYieldPrediction] = useState([]);
    const [dailyYieldPrediction, setDailyYieldPrediction] = useState([]);
    const [demandPrediction, setDemandPrediction] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastYearMarketData, setLastYearMarketData] = useState([]);
    const [weatherData, setWeatherData] = useState(null);

    const DAYS = 42;

    const handleBack = () => {
        try {
            navigate(-1);
        } catch (error) {
            console.error('Error navigating back:', error);
        }
    };

    const handlePredict = useCallback(async (temp, humid, co2Level) => {
        const greenhouse_name = greenhouse;
        const plant_date = plantingDate;
        let current_date = new Date();
        const weeks = Math.ceil(DAYS / 7);
        const weeklyPredictions = [];

        if (!isNaN(temp) && !isNaN(humid) && !isNaN(co2Level)) {
            setLoading(true);
            try {
                for (let i = 0; i < weeks; i++) {
                    const input_date = current_date.toISOString().split('T')[0];
                    const result = await fetchYieldPrediction(greenhouse_name, plant_date, input_date, temp, humid, co2Level, 1);
                    weeklyPredictions.push(result[0]);
                    setYieldPrediction([...weeklyPredictions]);
                    current_date.setDate(current_date.getDate() + 7); // 次の週に進む  
                }
            } catch (error) {
                console.error('Error fetching weekly prediction:', error);
            } finally {
                setLoading(false);
            }
        }
    }, [greenhouse, plantingDate]);

    const fetchDailyYieldPredictionHandler = useCallback(async (temp, humid, co2Level) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const greenhouse_name = greenhouse;
        const plant_date = plantingDate;
        let current_date = new Date();
        setLoading(true);

        const dailyPredictions = [];
        const days = DAYS;
        for (let i = 0; i < days; i++) {
            const input_date = current_date.toISOString().split('T')[0];
            try {
                const data = await fetchDailyYieldPrediction(greenhouse_name, plant_date, input_date, temp, humid, co2Level, 1, controller.signal);
                dailyPredictions.push(data[0]);
                setDailyYieldPrediction([...dailyPredictions]);
                current_date.setDate(current_date.getDate() + 1);
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching daily yield prediction:', error);
                break;
            }
        }
        setLoading(false);
        abortControllerRef.current = null;
    }, [greenhouse, plantingDate]);

    const fetchMarketforecastHandler = useCallback(async (startDate, days) => {  
        const results = [];  
        for (let i = 0; i < days; i++) {  
            const date = new Date(startDate);  
            date.setDate(date.getDate() + i);  
            const year = date.getFullYear();  
            const month = (date.getMonth() + 1).toString().padStart(2, '0');  
            const day = date.getDate().toString().padStart(2, '0');  
            const dateString = `${year}-${month}-${day}`;  
            try {  
                const data = await fetchMarketforecast(dateString, 1);  
                results.push(data[0]);  
                setDemandPrediction([...results]);  
            } catch (error) {  
                console.error('Error fetching demand prediction:', error);  
                break;  
            }  
        }  
    }, []);  

    useEffect(() => {
        const fetchSensorDataHandler = async () => {  
            try {  
                const data = await fetchSensorData(greenhouse);  
          
                // センサーデータの取得とデフォルト値の設定  
                let temp = data.InAirTemp;  
                let humid = data.InAirHumid;  
                let co2 = data.InAirCO2;  
          
                if (temp == null || isNaN(temp)) {  
                    console.warn('温度データが見つかりません。デフォルト値を使用します。');  
                    temp = 25;  
                } else {  
                    temp = Math.round(temp);  
                }  
          
                if (humid == null || isNaN(humid)) {  
                    console.warn('湿度データが見つかりません。デフォルト値を使用します。');  
                    humid = 60;  
                } else {  
                    humid = Math.round(humid);  
                }  
          
                if (co2 == null || isNaN(co2)) {  
                    console.warn('CO₂データが見つかりません。デフォルト値を使用します。');  
                    co2 = 400;  
                } else {  
                    co2 = Math.round(co2);  
                }  
          
                // 状態を更新  
                setTemperature(temp);  
                setHumidity(humid);  
                setCo2(co2);  
          
                // 予測を実行（並行して実行する）  
                const yieldPredictionPromise = handlePredict(temp, humid, co2);  
                const dailyYieldPredictionPromise = fetchDailyYieldPredictionHandler(temp, humid, co2);  
                const demandPredictionPromise = fetchMarketforecastHandler(new Date(), DAYS);  
          
                // すべての非同期処理が完了するのを待つ  
                await Promise.all([  
                    yieldPredictionPromise,  
                    dailyYieldPredictionPromise,  
                    demandPredictionPromise,  
                ]);  
          
                // 市況予測データをstateに設定  
                const demandPredictions = await demandPredictionPromise;  
                if (demandPredictions) {  
                    setDemandPrediction(demandPredictions);  
                }  
            } catch (error) {  
                console.error('センサーデータの取得中にエラーが発生しました:', error);  
            }  
        };  
        fetchSensorDataHandler();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [handlePredict, fetchDailyYieldPredictionHandler, fetchMarketforecastHandler, greenhouse]);

    const initialStartDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0];
    const initialEndDate = new Date(new Date(initialStartDate).setDate(new Date(initialStartDate).getDate() + DAYS)).toISOString().split('T')[0];

    useEffect(() => {
        const fetchData = async () => {
            const months = 12;
            const lastYearMarketData = await fetchLastYearMarketData(itemName, months);
            const filteredMarketData = lastYearMarketData.filter(data => {
                const date = new Date(data.TargetDate);
                const startDate = new Date(initialStartDate);
                const endDate = new Date(initialEndDate);

                return date >= startDate && date <= endDate;
            });

            setLastYearMarketData(filteredMarketData);
        };
        fetchData();
    }, [itemName, initialStartDate, initialEndDate]);

    const yieldLabels = yieldPrediction.map((prediction) => prediction.date);
    const yieldData = {
        labels: yieldLabels,
        datasets: [
            {
                label: '週別収量予測 (kg)',
                data: yieldPrediction.map((prediction, index) => {
                    const predictionDate = new Date(prediction.date);
                    const daysSincePlanting = (predictionDate - new Date(plantingDate)) / (1000 * 60 * 60 * 24);
                    return daysSincePlanting <= 33 ? 0 : prediction.prediction[0];
                }),
                backgroundColor: 'rgba(0, 128, 0, 0.5)',
            },
        ],
    };

    const dailyYieldLabels = dailyYieldPrediction.map((prediction) => prediction.date);
    const dailyYieldData = {
        labels: dailyYieldLabels,
        datasets: [
            {
                label: '日付別収量予測 (kg)',
                data: dailyYieldPrediction.map((prediction) => {
                    const predictionDate = new Date(prediction.date);
                    const daysSincePlanting = (predictionDate - new Date(plantingDate)) / (1000 * 60 * 60 * 24);
                    return daysSincePlanting <= 33 ? 0 : (prediction?.prediction[0] || 0);
                }),
                backgroundColor: 'rgba(0, 128, 0, 0.5)',
            },
        ],
    };

    const startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0];
    const endDate = new Date(new Date(startDate).setDate(new Date(startDate).getDate() + DAYS - 1)).toISOString().split('T')[0];
    const filledLastYearData = fillMissingDates(farmDataday[greenhouse], startDate, endDate);
    const lastYearLabels = filledLastYearData.map((data) => data.Date);
    const lastYearData = {
        labels: lastYearLabels,
        datasets: [
            {
                label: '昨年の収量 (kg)',
                data: filledLastYearData.map((data) => data.kg),
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                borderColor: 'rgba(54, 162, 235, 1)',
            },
        ],
    };

    const demandLabels = demandPrediction.map((prediction) => prediction.date);
    const demandData = {
        labels: demandLabels,
        datasets: [
            {
                label: '市況予測 (円)',
                data: demandPrediction.map((prediction) => prediction.result[0]),
                backgroundColor: 'rgba(0, 128, 0, 0.5)',
                borderColor: 'rgba(0, 128, 0, 0.5)',
                borderWidth: 1,
            },
        ],
    };

    const lastYearMarketLabels = lastYearMarketData.map(data => new Date(data.TargetDate).toISOString().split('T')[0]);
    const lastYearMarketChartData = {
        labels: lastYearMarketLabels,
        datasets: [{
            label: '昨年の市況 (円)',
            data: lastYearMarketData.map(data => data.NationalAveragePrice),
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
        }]
    };

    const chartOptions = {
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                anchor: 'end',
                align: 'end',
                formatter: Math.round,
                font: {
                    weight: 'bold',
                },
            },
        },
    };

    useEffect(() => {
        const controller = new AbortController();
        const fetchWeatherDataAsync = async () => {
            try {
                const data = await fetchForecastWeatherData(farm.location, farm.lat, farm.lon);
                setWeatherData(data);
            } catch (error) {
                console.error('Error Weather data:', error);
            }
        };
        fetchWeatherDataAsync();
        return () => controller.abort();
    }, [farm.location, farm.lat, farm.lon]);

    return (
        <Container>
            <AiText>Ai予測 {farmname}モデル</AiText>
            <InputContainer>
                <InputFieldsRow>

                    <InputFieldContainer>
                        <InputLabel>定植日</InputLabel>
                        <DateInput
                            type="date"
                            value={plantingDate}
                            onChange={(e) => setPlantingDate(e.target.value)}
                        />
                    </InputFieldContainer>
                    <InputFieldContainer>
                        <InputLabel>温度°C</InputLabel>
                        <SelectField
                            value={temperature}
                            onChange={(e) => setTemperature(e.target.value)}
                        >
                            <option value="" disabled></option>
                            {Array.from({ length: 26 }, (_, i) => 15 + i)
                                .reverse()
                                .map((temp) => (
                                    <option key={temp} value={temp}>
                                        {temp}
                                    </option>
                                ))}
                        </SelectField>
                    </InputFieldContainer>
                    <InputFieldContainer>
                        <InputLabel>湿度%</InputLabel>
                        <SelectField value={humidity} onChange={(e) => setHumidity(e.target.value)}>
                            <option value="" disabled></option>
                            {Array.from({ length: 81 }, (_, i) => 20 + i)
                                .reverse()
                                .map((hum) => (
                                    <option key={hum} value={hum}>
                                        {hum}
                                    </option>
                                ))}
                        </SelectField>
                    </InputFieldContainer>
                    <InputFieldContainer>
                        <InputLabel>CO２ppm</InputLabel>
                        <SelectField value={co2} onChange={(e) => setCo2(e.target.value)}>
                            <option value="" disabled></option>
                            {Array.from({ length: 501 }, (_, i) => 400 + i)
                                .reverse()
                                .map((co2Level) => (
                                    <option key={co2Level} value={co2Level}>
                                        {co2Level}
                                    </option>
                                ))}
                        </SelectField>
                    </InputFieldContainer>
                </InputFieldsRow>
                <Button
                    onClick={() => {
                        handlePredict(parseFloat(temperature), parseFloat(humidity), parseFloat(co2));
                        fetchDailyYieldPredictionHandler(parseFloat(temperature), parseFloat(humidity), parseFloat(co2));
                    }}
                >
                    収量予測
                </Button>
            </InputContainer>
            <ChartTitle>{DAYS / 7}週間先の週間収量予測</ChartTitle>
            <ChartWrapper>
                <ChartContainer>
                    <Bar data={yieldData} options={chartOptions} plugins={[ChartDataLabels]} height={300} />
                </ChartContainer>
            </ChartWrapper>
            <ChartTitle>{DAYS}日先までの収量予測</ChartTitle>
            <ChartWrapper>
                <ChartContainer>
                    <Bar data={dailyYieldData} options={chartOptions} plugins={[ChartDataLabels]} height={300} />
                </ChartContainer>
            </ChartWrapper>
            <ChartTitle>昨年同時期の収量実績</ChartTitle>
            <ChartWrapper>
                <ChartContainer>
                    <Bar data={lastYearData} options={chartOptions} plugins={[ChartDataLabels]} height={300} />
                </ChartContainer>
            </ChartWrapper>
            <ChartTitle>{DAYS}日先までの市況価格予測</ChartTitle>
            <ChartWrapper>
                <ChartContainer>
                    <Line data={demandData} options={chartOptions} plugins={[ChartDataLabels]} height={300} />
                </ChartContainer>
            </ChartWrapper>
            <ChartTitle>昨年同時期の市況価格</ChartTitle>
            <ChartWrapper>
                <ChartContainer>
                    <Line data={lastYearMarketChartData} options={chartOptions} plugins={[ChartDataLabels]} height={300} />
                </ChartContainer>
            </ChartWrapper>
            {weatherData && weatherData.forecasts && (
                <ForecastCard forecasts={weatherData.forecasts} summary={weatherData.summary} />
            )}
            <Button onClick={handleBack}>前の画面に戻る</Button>
            {loading && (
                <LoaderOverlay>
                    <Oval
                        height={80}
                        width={80}
                        color="#4fa94d"
                        wrapperStyle={{}}
                        wrapperClass=""
                        visible={true}
                        ariaLabel="oval-loading"
                        secondaryColor="#4fa94d"
                        strokeWidth={2}
                        strokeWidthSecondary={2}
                    />
                    <div style={{ marginTop: '10px', fontSize: '18px', color: '#4fa94d' }}>予測中</div>
                </LoaderOverlay>
            )}
        </Container>
    );
};

export default Ai;  
